import { useMutation, useQuery } from "@tanstack/react-query";
import { api, apiUrl } from "../lib/api";

const KEY = "special-days";

export type SpecialDayType = "public" | "bank" | "school" | "optional" | "observance";

export interface SpecialDayDesign {
	language: string;
	fileName: string;
	path: string;
	contentType: string;
	size: number | null;
	modified: string;
	isFallback: boolean;
}

export interface SpecialDay {
	countryCode: string;
	date: string;
	name: string;
	designKey: string;
	type: SpecialDayType;
	rule: string;
	substitute: boolean;
	design: SpecialDayDesign | null;
	hasDesignFolder: boolean;
	expectedPath: string;
}

export interface SpecialDaysResponse {
	days: SpecialDay[];
	nextcloud: { configured: boolean; baseMissing: boolean; error: string | null };
	base: string;
	range: { from: string; to: string };
}

export interface ContactCountry {
	code: string;
	companyCount: number;
	rawValues: string[];
	supported: boolean;
}

export interface SpecialDaysFilters {
	from: string;
	to: string;
	countries?: string[] | undefined;
}

export function useSpecialDays(filters: SpecialDaysFilters) {
	return useQuery({
		queryKey: [KEY, filters],
		queryFn: () => {
			const params = new URLSearchParams({ from: filters.from, to: filters.to });
			if (filters.countries?.length) params.set("countries", filters.countries.join(","));
			return api.get<SpecialDaysResponse>(`/api/special-days?${params.toString()}`);
		},
		staleTime: 5 * 60 * 1000,
	});
}

export function useContactCountries() {
	return useQuery({
		queryKey: [KEY, "countries"],
		queryFn: () => api.get<{ items: ContactCountry[] }>("/api/special-days/countries"),
		staleTime: 10 * 60 * 1000,
	});
}

export interface SpecialDayRecipient {
	companyId: string;
	companyName: string;
	email: string;
}

export interface SpecialDayRecipientsResponse {
	country: string;
	recipients: SpecialDayRecipient[];
	senderName: string;
	senderEmail: string;
	mailConfigured: boolean;
	signatureMissingReason: string | null;
}

export interface SendSpecialDayEmailInput {
	country: string;
	designPath: string;
	subject: string;
	message: string;
	emails: string[];
}

export interface SendSpecialDayEmailResult {
	sent: number;
	failed: { email: string; error: string }[];
}

export function useSpecialDayRecipients(country: string | null) {
	return useQuery({
		queryKey: [KEY, "recipients", country],
		queryFn: () =>
			api.get<SpecialDayRecipientsResponse>(
				`/api/special-days/recipients?country=${encodeURIComponent(country ?? "")}`,
			),
		enabled: !!country,
		staleTime: 60 * 1000,
	});
}

export function useSendSpecialDayEmail() {
	return useMutation({
		mutationFn: (input: SendSpecialDayEmailInput) =>
			api.post<SendSpecialDayEmailResult>("/api/special-days/send-email", input),
	});
}

export function designPreviewUrl(path: string): string {
	return apiUrl(`/api/special-days/design?path=${encodeURIComponent(path)}`);
}

export function designDownloadUrl(path: string): string {
	return apiUrl(`/api/special-days/design?download=1&path=${encodeURIComponent(path)}`);
}
